<template>
  <el-main>
    <el-row type="flex" id="admin-organizations" :gutter="32">
      <el-col :md="4" :sm="8">
        <div class="filter-menu">
          <el-menu default-active="1" @select="onMenuChange" ref="menu" class="sidemenu filter-side-menu">
            <el-menu-item index="1" filter="all">
              <all-icon />
              <span>{{ $t('admin.organizations.filter_menu.all') }}</span>
            </el-menu-item>
            <el-menu-item index="2" filter="active">
              <active-icon />
              <span>{{ $t('admin.organizations.filter_menu.active') }}</span>
            </el-menu-item>
            <el-menu-item index="3" filter="inactive">
              <disable-icon />
              <span>{{ $t('admin.organizations.filter_menu.disabled') }}</span>
            </el-menu-item>
            <el-menu-item index="4" filter="verified">
              <verified-icon />
              <span>{{ $t('admin.organizations.filter_menu.verified') }}</span>
            </el-menu-item>
          </el-menu>
          <el-select 
            v-model="filterCountrySelected" 
            @change="filterHandler"
            class="filter-country" 
            filterable collapse-tags multiple 
            :placeholder="$t('admin.organizations.filter_menu.filter_by_country')"
          >
            <el-option 
              v-for="(country, idx) in getCountries" 
              :key="idx" 
              :label="country.name" 
              :value="country.iso2"
            />
          </el-select>
          <el-select 
            v-model.lazy="filterOrgTypeSelected"
            @change="filterHandler"
            class="filter-country" 
            :placeholder="$t('admin.organizations.filter_menu.filter_by_org_type')"
            multiple
          >
            <el-option 
              v-for="(type, idx) in orgTypes" 
              :key="idx" 
              :label="type.label" 
              :value="type.value"
            />
          </el-select>
        </div>
      </el-col>
      <el-col :md="20" :sm="24">
        <div class="organization-table">
          <h1>{{ $t('admin.organizations.title') }}</h1>
          <el-form>
            <elsa-search v-model="queryString" @search="searchOrganization" />
          </el-form>
          <div class="actions">
            <div>
              <chip icon="elsa-icon-plus" @click.native="openCreateOrganizationModal">
                {{ $t('admin.organizations.create_new_organization') }}
              </chip>
              <!-- <chip icon="elsa-icon-import">
                {{ $t('admin.organizations.import_organizations') }}
              </chip> -->
            </div>
            <div v-if="showBulkOptions" class="bulk-actions">
              <div @click="verifyAllOrganizations">
                <chip-icon icon="elsa-icon-yellow-verified" :tooltip="$t('admin.organizations.verify')" />
              </div>
            </div>
          </div>
          <div class="organization-list">
            <base-table-loading
              v-if="loading"
              :data="skeletonData"
            />
            <div v-else class="table-wrapper">
              <over-table-pagination class="over-table-pagination-right" :total="pagination.total" :currentPage="currentPage" :pageSize="maxNumberOfElements" />
              <el-table ref="multipleTable" :data="organizations" @select-all="handleSelectRow" @select="handleSelectRow" style="width: 100%" :empty-text="$t('admin.organizations.table.empty')" @row-click="showDetails" :default-sort="{ prop: 'name', order: 'ascending' }">
                <el-table-column type="selection" />
                <organization-table-row sortable :sort-by="sortByName" prop="name" width="280" type="name" :label="$t('admin.organizations.table.organization_name')" />
                <organization-table-row sortable :sort-by="sortByUnredeemedLicenses" width="230" type="unredeemed_licenses" :label="$t('admin.organizations.table.unredeemed_licenses')" />
                <organization-table-row sortable :sort-by="sortByVerification" prop="verification" width="202" type="verification" :label="$t('admin.organizations.table.verification')" />
                <organization-table-row sortable :sort-by="sortByStatus" prop="status" width="202" type="status" :label="$t('admin.organizations.table.status')" />
                <!-- <organization-table-row type="licences" :label="$t('admin.organizations.table.licences')" /> -->
                <organization-table-row sortable type="created_at" :sort-by="sortByCreatedAt" :label="$t('admin.organizations.table.created_at')" />
                <organization-table-row sortable type="contract_end" :sort-by="sortByContractEnd" :label="$t('admin.organizations.table.contract_end')" />
                <organization-table-row sortable type="expected_renewal" :sort-by="sortByExpectedRenewal" :label="$t('admin.organizations.table.expected_renewal')" />
                <organization-table-row sortable type="country" :sort-by="sortByCountry" :label="$t('admin.organizations.table.country')" />
                <el-table-column sortable :label="$t('common.action')" type="invite">
                  <template slot-scope="scope">
                    <div class="invite-owner" @click="handleAddAdmin(scope.row)">
                      <i class="el-icon-circle-plus-outline" />
                      {{ $t('admin.invite_admin') }}
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <div class="pagination">
              <pagination :page-size="maxNumberOfElements" :total="pagination.total" :pager-counter="5" v-on:current-change="updateCurrentPage" />
              <over-table-pagination class="over-table-pagination-right" :total="pagination.total" :currentPage="currentPage" :pageSize="maxNumberOfElements" />
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
    <create-edit-organization :dialogVisible="modals.createOrganizationModal" v-on:closed="modals.createOrganizationModal = false" :fromAdmin="true" />
    <invite-admin :dialogVisible="modals.showInviteAdminModal" v-on:closed="modals.showInviteAdminModal = false" :organizationId="inviteAdminToOrgId" roleAdmin="admin" />
  </el-main>
</template>

<script>
import Pagination from '@/components/common/Pagination'
import OverTablePagination from '@/components/common/OverTablePagination'
import CreateEditOrganization from '@/components/modals/organizations/CreateEditOrganization'
import OrganizationTableRow from '@/components/common/OrganizationTableRow'
import ActiveIcon from '@/assets/images/inline.active-icon.svg'
import AllIcon from '@/assets/images/inline.all-icon.svg'
import DisableIcon from '@/assets/images/inline.disable-icon.svg'
import VerifiedIcon from '@/assets/images/inline.verified-icon.svg'
import Chip from '@/components/common/Chip'
import ChipIcon from '@/components/common/ChipIcon'
import ElsaSearch from '@/components/common/Search'
import { NUMBER_ELEMENTS_PER_PAGE } from '@/constants/generic'
import { mapActions, mapGetters } from 'vuex'
import { countries } from '@/helpers/countries'
import InviteAdmin from '@/components/modals/organizations/InviteAdmin'
import BaseTableLoading from '@/components/common/BaseTableLoading.vue';
import { SELECT_ORGANIZATION_TYPE_FOR_ADMIN, SCHOOL_OR_UNIVERSITY, SCHOOL } from '@/constants/organizationType';
import { isEmpty, not, propOr, compose, toLower, reject, either, isNil, map, filter, propEq, join } from 'ramda'

export default {
  components: {
    BaseTableLoading,
    CreateEditOrganization,
    OrganizationTableRow,
    Chip,
    Pagination,
    ActiveIcon,
    AllIcon,
    DisableIcon,
    OverTablePagination,
    ChipIcon,
    VerifiedIcon,
    ElsaSearch,
    InviteAdmin
  },
  computed: {
    showBulkOptions: compose(not, isEmpty, propOr([], 'selectedOrganizations')),
    ...mapGetters({
      organizations: 'admin/getOrganizations',
      pagination: 'admin/buildPagination',
      organizationsSize: 'admin/adminOrganizationsSize'
    }),
    maxNumberOfElements() {
      return NUMBER_ELEMENTS_PER_PAGE
    },
    ...mapGetters('user', ['countryAdminLevel']),
    getCountries() {
      if (this.countryAdminLevel) return filter(propEq('iso2', this.countryAdminLevel), countries)
      return countries
    }
  },
  data() {
    return {
      queryString: '',
      filter: 'all',
      selectedOrganizations: [],
      currentPage: 1,
      filterParams: {},
      modals: {
        createOrganizationModal: false,
        showInviteAdminModal: false
      },
      organizationComponent: 'organization',
      filterCountrySelected: [],
      inviteAdminToOrgId: null,
      loading: true,
      skeletonData: [
        {
          name: "Name",
          label: "Organization name",
          layout: [
            {
              width: "30%",
              justifyContent: "center",
              alignItems: "center",
              items: [
                {
                  width: "40px",
                  height: "40px",
                  type: "circle",
                },
              ],
            },
            {
              width: "70%",
              items: [
                {
                  type: "rectangle",
                  width: "60%",
                  height: "12px",
                },
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Class",
          label: "Unredeemed Licenses",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Verification",
          label: "Verification",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Status",
          label: "Status",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Creation date",
          label: "Creation date",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Contract End",
          label: "Contract End",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Expected Renewal",
          label: "Expected Renewal",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Country",
          label: "Country",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Action",
          label: "Action",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
      ],
      filterOrgTypeSelected: [],
      orgTypes: SELECT_ORGANIZATION_TYPE_FOR_ADMIN,
    }
  },
  methods: {
    async updateCurrentPage(page) {
      try {
        this.loading = true
        this.currentPage = page
        await this.getOrganizations({ page, ...this.filterParams })
      } finally {
        this.loading = false
      }
    },

    openCreateOrganizationModal() {
      this.modals.createOrganizationModal = true
    },
    handleSelectRow(rowData) {
      this.selectedOrganizations = rowData
    },
    showDetails(row, column) {
      if (column.type === 'verified') {
        this.verifyOrganization(row)
        return
      }
      if (column.type === 'invite') {
        this.handleAddAdmin(row)
        return
      }
      this.$store.dispatch('organization/setCurrentOrganization', { organizationId: row.id })
      this.$store.dispatch('organization/loadUserOrganizationsData')

      this.setCurrentOrganization(row)
      this.$router.push({
        path: `/admin/organization/${row.id}/`
      })
    },
    onMenuChange(index) {
      const filter = this.$refs.menu.items[index].$attrs.filter
      if (['active', 'inactive'].includes(filter)) {
        this.filterParams = { ...this.filterParams, status: filter, is_verified: null }
      } else if (filter === 'verified') {
        this.filterParams = { ...this.filterParams, is_verified: true, status: null }
      } else {
        this.filterParams = { ...this.filterParams, status: '' }
      }
      this.getOrganizations(this.filterParams)
    },
    searchOrganization(name) {
      this.filterParams = { ...this.filterParams, search: name }
      this.getOrganizations(this.filterParams)
    },
    async verifyOrganization(row) {
      try {
        const org = { ...row }
        org.is_verified = !org.is_verified
        const organizationData = reject(either(isNil, isEmpty))(org)
        await this.updateOrganization({ id: org.id, organization: organizationData })
      } catch (error) {
        error
      }
    },
    verifyAllOrganizations() {
      return map(this.verifyOrganization)(this.selectedOrganizations)
    },
    ...mapActions({
      setCurrentOrganization: 'admin/setCurrentOrganization',
      updateOrganization: 'admin/updateOrganization',
      getOrganizationTypes: 'organization/getOrganizationTypes',
      getOrganizations: 'admin/getOrganizations'
    }),
    sortByName: compose(toLower, propOr('', 'name')),
    sortByStatus: compose(toLower, propOr('', 'status')),
    sortByCreatedAt(row) {
      return new Date(row.created_at)
    },
    sortByContractEnd(row) {
      if (row.contract && row.contract.contract_start_date) {
        return new Date(row.contract.contract_start_date)
      } else {
        return null
      }
    },
    sortByUnredeemedLicenses(row) {
      let x = 0
      if (row && row.payment && row.payment.plan_details && row.payment.plan_details.length > 0) {
        row.payment.plan_details.forEach((element) => {
          x = x + (element.number_of_learner - element.number_of_license_used)
        })
      }
      return x
    },
    sortByExpectedRenewal(row) {
      return this.sortByContractEnd(row)
    },
    sortByCountry: compose(toLower, propOr('', 'country')),
    sortByVerification: compose(String, propOr(false, 'is_verified')),
    filterHandler() {
      let orgTypes = [...this.filterOrgTypeSelected]
      if (orgTypes.indexOf(SCHOOL) > -1 || orgTypes.indexOf(SCHOOL_OR_UNIVERSITY) > -1) {
        orgTypes.push(SCHOOL)
        orgTypes.push(SCHOOL_OR_UNIVERSITY)
        orgTypes = [...new Set(orgTypes)]
      }
      this.filterParams = { 
        ...this.filterParams, 
        country: join(',', this.filterCountrySelected),
        types: join(',', orgTypes) 
      }
      this.getOrganizations(this.filterParams)
    },
    handleAddAdmin(org) {
      this.modals.showInviteAdminModal = true
      this.inviteAdminToOrgId = org.id
    }
  },
  async mounted() {
    try {
      this.loading = true
      if (this.$store.state.organization.types.length === 0) {
        this.getOrganizationTypes()
      }
      await this.getOrganizations()
    } finally {
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/_colors.scss';

@media only screen and (max-width: 992px) {
  .organization-table {
    margin-top: 30px;
  }
}

#admin-organizations {
  margin-top: 48px;
  flex-wrap: wrap;
}

.search-box {
  margin-top: 12px;
}

.filter-menu {
  margin-top: 40px;
  .filter-country {
    margin-top: 20px;
  }
}

.el-form {
  margin-top: 16px;
  padding-bottom: 0;
}

h1 {
  border: none;
}
.elsa-chip {
  background-color: white;
  &:first-of-type {
    width: 270px;
  }
}
.actions {
  display: flex;
  margin-top: 24px;
  border-top: 1px solid rgba(206, 224, 234, 0.5);
  padding-top: 24px;
  .bulk-actions {
    margin-right: 0;
    .elsa-chip {
      width: auto;
      margin: 0;
    }
  }
  div {
    line-height: 22px;
    display: flex;
    align-items: center;
    margin-right: 24px;
    color: $purple;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    i {
      border-width: 2px;
      font-size: 18px;
      margin: auto 0;
      margin-right: 5px;
    }
  }
}

.search-orgs.el-input::v-deep .el-input__inner {
  padding-left: 16px;
}

.bottom-list {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 38px;
}

::v-deep .el-table__row {
  &:hover .verified {
    display: block;
  }
  .verified {
    display: none;
  }
}
</style>
